import 'react';
import { useMemo } from 'react';
import { FeatureFlagName } from 'shared/enums';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { integrationSystemDisplayName, useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import {
  GetProviderAppointmentTypeRoomMapQuery,
  useGetProviderAppointmentTypeRoomMapQuery,
} from 'shared/types/graphql';

export type IProviderAppointmentTypeRoomMap = NonNullable<
  GetProviderAppointmentTypeRoomMapQuery['findManyProviderAppointmentTypeRoomMap']
>[0];

export const useProviderAppointmentTypeRoomMap = (): {
  providerApptTypeRoomMaps: IProviderAppointmentTypeRoomMap[];
  isSupported: boolean;
  isRequired: boolean;
} => {
  const { currentClinicId, hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();
  const { primaryIntegrationName } = useIntegrationsProvider();

  const { data: providerApptTypeRoomMapData } = useGetProviderAppointmentTypeRoomMapQuery({
    variables: {
      clinicId: currentClinicId || '',
    },
    skip: !isFeatureEnabled(FeatureFlagName.DirectBooking) || !currentClinicId || !hasAdminRoleAtCurrentClinic,
  });

  const providerApptTypeRoomMaps = useMemo(
    () => providerApptTypeRoomMapData?.findManyProviderAppointmentTypeRoomMap ?? [],
    [providerApptTypeRoomMapData?.findManyProviderAppointmentTypeRoomMap],
  );

  /** Room mapping is supported by Cornerstone and Avimark */
  const isSupported = useMemo(
    () =>
      !!primaryIntegrationName
        ? [integrationSystemDisplayName.CORNERSTONE, integrationSystemDisplayName.AVIMARK].includes(
            primaryIntegrationName,
          )
        : false,
    [primaryIntegrationName],
  );

  /** Room mapping is only required by Cornerstone, and is optional for Avimark */
  const isRequired = useMemo(
    () => primaryIntegrationName === integrationSystemDisplayName.CORNERSTONE,
    [primaryIntegrationName],
  );

  return {
    providerApptTypeRoomMaps,
    isSupported,
    isRequired,
  };
};
